import { useRef, useState, type KeyboardEvent } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { MiniCalendar, formatWeekdayDate, parseDateOnly, type CalendarMonth } from '@odo/shared-ui';
import { FloatingMenu } from '../FloatingMenu';

interface AppointmentDateFieldProps {
    value: string;
    onChange: (date: string) => void;
    todayIso: string;
    disabled?: boolean;
    id?: string;
    /** Error border for inline validation; omitted by callers without field errors. */
    invalid?: boolean;
    /** Earliest pickable day ('YYYY-MM-DD'); earlier days show disabled. */
    minDate?: string;
}

function monthFromIso(dateOnly: string): CalendarMonth {
    const date = parseDateOnly(dateOnly);
    return { year: date.getFullYear(), month: date.getMonth() };
}

const NO_COUNTS: Record<string, number> = {};
const CALENDAR_ESTIMATED_HEIGHT = 360;

// Port of the dashboard Composer's DateField, minus per-day count dots; the calendar portals
// via FloatingMenu because hosts sit in their own scroll containers.
export function AppointmentDateField({
    value,
    onChange,
    todayIso,
    disabled,
    id,
    invalid = false,
    minDate
}: AppointmentDateFieldProps) {
    const borderClasses = invalid
        ? 'border-red-300 hover:border-red-400 focus:border-red-500'
        : 'border-gray-200 hover:border-gray-400 focus:border-gray-400';
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState<CalendarMonth>(() => monthFromIso(value || todayIso));
    const triggerRef = useRef<HTMLButtonElement>(null);

    const close = () => setOpen(false);

    const handleToggle = () => {
        if (disabled) return;
        setOpen((wasOpen) => {
            if (!wasOpen) setMonth(monthFromIso(value || todayIso));
            return !wasOpen;
        });
    };

    // Consume Escape from the trigger or the portalled calendar, so it closes only the calendar
    // and not the host modal or task surface listening on document.
    const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
        if (e.key !== 'Escape' || !open) return;
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
    };

    return (
        <>
            <button
                ref={triggerRef}
                id={id}
                type="button"
                onClick={handleToggle}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={open}
                className={`w-full flex items-center gap-2 rounded-lg border ${borderClasses} bg-white px-3 py-2.5 text-sm text-gray-800 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                <CalendarDaysIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="flex-1 text-left truncate">
                    {value ? formatWeekdayDate(value) : 'Select a date'}
                </span>
            </button>
            <FloatingMenu
                open={open}
                triggerRef={triggerRef}
                onClose={close}
                estimatedHeight={CALENDAR_ESTIMATED_HEIGHT}
            >
                {/* Focusable, so a click on padding or a disabled day keeps focus (and Escape) here. */}
                <div
                    tabIndex={-1}
                    onKeyDown={handleKeyDown}
                    className="w-[300px] rounded-xl border border-gray-200 bg-white shadow-lg p-4 outline-none"
                >
                    <MiniCalendar
                        month={month}
                        onMonthChange={setMonth}
                        counts={NO_COUNTS}
                        selected={value}
                        todayIso={todayIso}
                        minDate={minDate}
                        onSelect={(day) => {
                            if (day) onChange(day);
                            close();
                        }}
                    />
                </div>
            </FloatingMenu>
        </>
    );
}
